import {
  Sidebar as ProSidebar, Menu,
} from 'react-pro-sidebar';
import styled from 'styled-components';
import {
  PropsWithChildren, ReactNode, RefObject, useEffect, useState,
} from 'react';
import { useStyledTheme } from '../../styling';
import { Flex, FlexColumn, FlexGrow } from '../Flex';
import { SidebarMenuItem } from '.';
import { useRefs } from '../Refs';
import { PageRefName } from '../Page';

export const SidebarDivider = styled.div`
  border-bottom: ${({ theme }) => theme.borderWidth[1]} solid ${({ theme }) => theme.colours.neutral[7]};
`;

const SidebarContentContainer = styled(FlexColumn)`
  top: 0;
  height:100%;
`;

const SidebarContent = styled(FlexGrow)`${({ theme }) => `
  background: ${theme.colours.neutral[9]};
  padding: 0;
  overflow-x:hidden;
  overflow-y:auto;
`}`;

const SidebarFooterContent = styled.div`
 overflow:auto;
`;

export const SidebarMenuTitle = styled.div`${({ theme }) => `
  font-family: Montserrat;
  flex: 1;
  font-size:${theme.fontSize[2]};
  font-weight:${theme.fontWeight.bold};
  padding:0;
  margin:0;
`}`;

export const SidebarTopText = styled.div` ${({ theme }) => `
  display:flex;
  color:${theme.colours.neutral[2]};
  font-size:${theme.fontSize[1]}; 
  font-weight:${theme.fontWeight.bold}; 
  text-overflow: ellipsis;
  overflow:hidden;
  white-space:nowrap;
`}`;

export const SidebarTopTextBold = styled.span` ${({ theme }) => `
  font-weight:${theme.fontWeight.bold};
`}`;

export const SidebarMenuItemDynamic = styled.div<{ highlight: boolean, hasSubSection: boolean }>`${({ theme, highlight, hasSubSection }) => `
  background: ${highlight && hasSubSection ? theme.colours.secondary[7] : ''};
  ${highlight ? `border-left: ${theme.borderWidth[3]} solid ${theme.colours.secondary[1]} !important;` : ''}
  :hover {
    background: ${theme.colours.neutral[8]};
  }
`}`;

export const TextContainer = styled(Flex)` ${({ theme }) => `
  gap:${theme.padding[1]};
  align-items:center;
`}`;

export function SidebarTitleItem({
  children,
  onClick = () => { },
  onScreen = false,
  icon,
  hasSubSection = false,
}: PropsWithChildren<{
  onClick?: () => void,
  onScreen: boolean,
  icon?: ReactNode,
  hasSubSection?: boolean,
}>) {
  return (
    <SidebarMenuItemDynamic onClick={onClick} highlight={onScreen} hasSubSection={hasSubSection}>
      <SidebarMenuItem>
        <SidebarMenuTitle>
          <TextContainer>
            {icon}
            {children}
          </TextContainer>
        </SidebarMenuTitle>
      </SidebarMenuItem>
    </SidebarMenuItemDynamic>
  );
}

export function useIsVisible(ref: RefObject<any>, stickyHeaderOffset: number) {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsIntersecting(entry.isIntersecting),
      {
        rootMargin: `-${stickyHeaderOffset + 16}px 0px -${window.innerHeight}px 0px`,
      },
    );

    if (ref?.current) observer.observe(ref?.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, stickyHeaderOffset]);

  return isIntersecting;
}

export type ScrollSidebarTitleItemProps = PropsWithChildren<{
  refName?: string,
  stickyHeaderOffset?: number,
  icon?: ReactNode,
  hasSubSection?: boolean,
}>;

export function ScrollSidebarTitleItem({
  children,
  refName = '',
  stickyHeaderOffset = 0,
  icon,
  hasSubSection = false,
}: ScrollSidebarTitleItemProps) {
  const { refs } = useRefs();

  const onClickSidebarItem = (rn: string) => {
    const { current } = refs?.[rn] ?? {};

    if (current) {
      refs[PageRefName]?.current.scrollTo({ top: (current?.offsetTop ?? 0) - stickyHeaderOffset, behavior: 'smooth' });
    }
  };

  const isOnScreen = useIsVisible(refs[refName], stickyHeaderOffset);

  return (
    <SidebarTitleItem onClick={() => onClickSidebarItem(refName)} onScreen={isOnScreen} icon={icon} hasSubSection={hasSubSection}>
      {children}
    </SidebarTitleItem>
  );
}

const CollapsibleTopBar = styled(Flex)`
  width:100%;
  justify-content:end;
`;

const CollapsibleIconContainer = styled(Flex)`${({ theme }) => `
  padding-right: ${theme.padding[1]};
  padding-top: ${theme.padding[2]};
  height: ${theme.padding[7]};  
  width: ${theme.padding[7]};
`}`;

const IconCircle = styled(Flex)`${({ theme }) => `
  width: ${theme.padding[6]};
  height: ${theme.padding[6]};
  border-radius: calc(${theme.padding[6]}/2);
  background: ${theme.colours.primary[1]};
  justify-content: center;
  align-items: center;
`}`;

const CollapsibleIcon = styled.i<{ collapsed: boolean}>` ${({ theme }) => `
  color: ${theme.colours.neutral[10]};
  font-size: ${theme.fontSize[5]};
  cursor: pointer;
`}`;

export function Sidebar({ sidebar, sidebarFooter, width }: { sidebar: ReactNode, sidebarFooter: ReactNode, width: string }) {
  const theme = useStyledTheme();

  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <ProSidebar
      width={width}
      rootStyles={{
        border: 'none',
        height: '100%',
      }}
      collapsed={isCollapsed}
      collapsedWidth="40px"
    >

      <SidebarContentContainer>
        <CollapsibleTopBar>
          <CollapsibleIconContainer>
            <IconCircle>
              <CollapsibleIcon
                className={isCollapsed ? 'mi-chevron-right' : 'mi-chevron-left'}
                collapsed={isCollapsed}
                onClick={() => setIsCollapsed((prev) => !prev)}
              />
            </IconCircle>
          </CollapsibleIconContainer>
        </CollapsibleTopBar>

        {!isCollapsed && (
        <>

          <SidebarContent>

            <Menu
              rootStyles={{
                padding: `0 ${theme.padding[4]} 0`,
                marginBottom: theme.padding[3],
                lineHeight: theme.fontSize[3],
                color: theme.colours.neutral[1],
                fontWeight: theme.fontWeight.normal,
                fontSize: theme.fontSize[1],
                flex: 'flex-grow',
              }}
              menuItemStyles={{
                subMenuContent: {
                  background: theme.colours.neutral[1],
                },
                root: () => ({
                  display: 'block',
                  width: 'auto',
                  background: 'rgba(0,0,0,0)',
                  borderLeft: `${theme.borderWidth[3]} solid transparent`,
                  '&:active': {
                    borderLeft: `${theme.borderWidth[3]} solid ${theme.colours.secondary[1]} !important`,
                  },
                }),
                label: ({ active }) => ({
                  whiteSpace: 'break-spaces',
                  color: theme.colours.neutral[2],
                  fontWeight: active ? theme.fontWeight.bold : theme.fontWeight.normal,
                }),
                button: ({ active, disabled }) => ({
                  borderRadius: theme.borderRadius[2],
                  margin: `${theme.padding[1]} calc(${theme.padding[4]} - ${theme.borderWidth[3]})`,
                  padding: `${theme.padding[4]} ${theme.padding[2]}`,
                  background: active ? theme.colours.warning[2] : 'rgba(0,0,0,0)',
                  fontSize: theme.fontSize[1],
                  height: theme.sidebarItemHeight,
                  color: !disabled ? theme.colours.primary[1] : theme.colours.neutral[5],
                  '&:hover': {
                    background: 'none',
                  },
                }),
                suffix: {
                  paddingLeft: theme.padding[4],
                  color: theme.colours.neutral[3],
                },
                icon: {
                  marginRight: `-${theme.borderWidth[1]}`,
                  lineHeight: 0,
                },
              }}
            >
              {sidebar}
            </Menu>

          </SidebarContent>

          <SidebarFooterContent><Menu>{sidebarFooter}</Menu></SidebarFooterContent>
        </>
        )}
      </SidebarContentContainer>
    </ProSidebar>
  );
}
